import React, { useEffect, useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { Copy, Download, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { resolveImageUrl } from "@/lib/api";
import { vendorUpdateShop } from "@/lib/vendorApi";

export const OFFER_MAX = 40;

// Shared by Dashboard + Tool Bag so both always copy the same canonical link.
export const shopPublicUrl = (shop) => {
  const key = shop?.slug || shop?.shop_id;
  return key ? `${window.location.origin}/v/${encodeURIComponent(key)}` : null;
};

export async function copyShopLink(shop) {
  const url = shopPublicUrl(shop);
  if (!url) { toast.error("Shop link is not available yet"); return; }
  try {
    if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(url);
    else {
      const t = document.createElement("textarea");
      t.value = url; document.body.appendChild(t); t.select(); document.execCommand("copy"); t.remove();
    }
    toast.success("Shop link copied");
  } catch { toast.error("Could not copy — please select the link and copy it"); }
}

const TXT = {
  en: { scan: "Scan to view our shop", live: "Scan for live Open / Closed status", timing: "Timing" },
  hi: { scan: "हमारी दुकान देखने के लिए स्कैन करें", live: "दुकान खुली / बंद की ताज़ा जानकारी के लिए स्कैन करें", timing: "समय" },
};
const FONT = "Arial, 'Noto Sans Devanagari', 'Mangal', sans-serif";

const loadLogo = (src) => new Promise((res) => {
  if (!src) return res(null);
  const img = new Image();
  img.crossOrigin = "anonymous";
  img.onload = () => res(img);
  img.onerror = () => res(null);
  img.src = resolveImageUrl(src);
});

function wrap(ctx, text, maxW, max = 3) {
  const lines = [];
  let line = "";
  for (const w of String(text).split(/\s+/)) {
    const t = line ? `${line} ${w}` : w;
    if (ctx.measureText(t).width > maxW && line) { lines.push(line); line = w; } else line = t;
  }
  if (line) lines.push(line);
  return lines.slice(0, max);
}

function text(ctx, s, x, y, size, color, weight = "bold", align = "center", maxW) {
  ctx.font = `${weight} ${size}px ${FONT}`; ctx.fillStyle = color; ctx.textAlign = align;
  const limit = maxW || (align === "center" ? ctx.canvas.width - 200 : ctx.canvas.width - x - 60);
  ctx.fillText(s, x, y, limit);
}

function logoBox(ctx, logo, x, y, s) {
  ctx.save();
  ctx.fillStyle = "#ffffff"; ctx.strokeStyle = "#e2e8f0"; ctx.lineWidth = Math.max(4, s / 60);
  ctx.fillRect(x, y, s, s); ctx.strokeRect(x, y, s, s);
  const r = Math.min(s / logo.width, s / logo.height), w = logo.width * r, h = logo.height * r;
  ctx.drawImage(logo, x + (s - w) / 2, y + (s - h) / 2, w, h);
  ctx.restore();
}

// Orders [primary, secondary] by chosen language.
const order = (lang, key) => (lang === "hi" ? [TXT.hi[key], TXT.en[key]] : [TXT.en[key], TXT.hi[key]]);

function drawA4({ qr, logo, shop, url, lang, offer }) {
  const W = 2480, H = 3508, c = document.createElement("canvas");
  c.width = W; c.height = H;
  const ctx = c.getContext("2d");
  ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, W, H);
  ctx.fillStyle = "#047857"; ctx.fillRect(0, 0, W, 60); ctx.fillRect(0, H - 60, W, 60);
  ctx.font = "bold 140px Georgia, serif"; ctx.fillStyle = "#047857"; ctx.textAlign = "center"; ctx.fillText("VerB", W / 2, 280);
  text(ctx, "Vertual Bazaar", W / 2, 360, 48, "#64748b", "600");
  let y = 440;
  if (logo) { logoBox(ctx, logo, (W - 420) / 2, y, 420); y += 420 + 60; } else y += 40;
  ctx.font = `bold 140px ${FONT}`;
  wrap(ctx, shop.name || "Our Shop", W - 360, 2).forEach((l) => { y += 150; text(ctx, l, W / 2, y, 140, "#0f172a"); });
  if (offer) { y += 140; text(ctx, offer, W / 2, y, 88, "#b45309"); }
  const qs = (logo ? 1350 : 1550) - (offer ? 150 : 0), qx = (W - qs) / 2, qy = y + 110;
  ctx.strokeStyle = "#f59e0b"; ctx.lineWidth = 14; ctx.strokeRect(qx - 60, qy - 60, qs + 120, qs + 120);
  ctx.imageSmoothingEnabled = false; ctx.drawImage(qr, qx, qy, qs, qs);
  y = qy + qs + 210;
  const [s1, s2] = order(lang, "scan");
  text(ctx, s1, W / 2, y, 115, "#047857"); text(ctx, s2, W / 2, y + 135, 80, "#334155", "600");
  y += 250;
  if (shop.hours_label) {
    const [t1] = order(lang, "timing");
    ctx.font = `600 72px ${FONT}`;
    wrap(ctx, `${t1}: ${shop.hours_label}`, W - 300, 2).forEach((l) => { text(ctx, l, W / 2, y, 72, "#0f172a", "600"); y += 90; });
    y += 10;
  }
  text(ctx, order(lang, "live")[0], W / 2, y, 56, "#64748b", "500");
  ctx.font = `52px ${FONT}`;
  wrap(ctx, url.replace(/^https?:\/\//, ""), W - 300, 2).forEach((l, i) => text(ctx, l, W / 2, y + 100 + i * 64, 52, "#64748b", "400"));
  return c;
}

// One A6 landscape panel (148×105 mm @300dpi = 1748×1240) drawn at origin.
function drawA6Panel(ctx, { qr, logo, shop, lang, offer }) {
  const W = 1748, H = 1240, pad = 80, qs = 820;
  ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, W, H);
  ctx.fillStyle = "#047857"; ctx.fillRect(0, 0, W, 34);
  ctx.strokeStyle = "#f59e0b"; ctx.lineWidth = 10; ctx.strokeRect(pad - 30, (H - qs) / 2 - 30, qs + 60, qs + 60);
  ctx.imageSmoothingEnabled = false; ctx.drawImage(qr, pad, (H - qs) / 2, qs, qs);
  const tx = pad + qs + 90, tw = W - tx - pad;
  let y = 230;
  if (logo) { logoBox(ctx, logo, tx, 120, 200); y = 400; }
  ctx.font = `bold 84px ${FONT}`;
  wrap(ctx, shop.name || "Our Shop", tw, 3).forEach((l) => { y += 95; text(ctx, l, tx, y, 84, "#0f172a", "bold", "left"); });
  ctx.font = `bold 64px ${FONT}`;
  y += 70;
  wrap(ctx, order(lang, "scan")[0], tw, offer ? 2 : 3).forEach((l) => { y += 78; text(ctx, l, tx, y, 64, "#047857", "bold", "left"); });
  if (offer) { ctx.font = `bold 50px ${FONT}`; wrap(ctx, offer, tw, 2).forEach((l) => { y += 64; text(ctx, l, tx, y, 50, "#b45309", "bold", "left"); }); }
  ctx.font = "bold 60px Georgia, serif"; ctx.fillStyle = "#047857"; ctx.textAlign = "left"; ctx.fillText("VerB", tx, H - 90);
}

function drawA6Tent(opts) {
  // A5 portrait sheet = two A6 panels; top panel upside-down so both faces read correctly after folding.
  const W = 1748, H = 2480, c = document.createElement("canvas");
  c.width = W; c.height = H;
  const ctx = c.getContext("2d");
  ctx.save(); ctx.translate(W, 1240); ctx.rotate(Math.PI); drawA6Panel(ctx, opts); ctx.restore();
  ctx.save(); ctx.translate(0, 1240); drawA6Panel(ctx, opts); ctx.restore();
  ctx.setLineDash([24, 18]); ctx.strokeStyle = "#94a3b8"; ctx.lineWidth = 4;
  ctx.beginPath(); ctx.moveTo(0, 1240); ctx.lineTo(W, 1240); ctx.stroke(); ctx.setLineDash([]);
  text(ctx, "✂ fold here / यहाँ मोड़ें", W - 40, 1300, 30, "#64748b", "600", "right");
  return c;
}

function downloadCanvas(c, name) {
  return new Promise((res, rej) => c.toBlob((blob) => {
    if (!blob) return rej(new Error("blob"));
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = name;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 1500);
    res();
  }, "image/png"));
}

const cleanOffer = (v) => String(v || "").replace(/[\u0000-\u001f<>]/g, "").slice(0, OFFER_MAX);

// Single poster engine for Dashboard + Tool Bag: preview canvas IS the downloaded file.
export function PosterStudio({ shop, onSaved, testid = "poster" }) {
  const qrRef = useRef(null);
  const canvasRef = useRef(null);
  const url = shopPublicUrl(shop);
  const [lang, setLang] = useState("en");
  const [size, setSize] = useState("a4");
  const [offer, setOffer] = useState(cleanOffer(shop?.poster_offer));
  const [logo, setLogo] = useState(undefined);
  const [preview, setPreview] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setOffer(cleanOffer(shop?.poster_offer)); }, [shop?.shop_id, shop?.poster_offer]);
  useEffect(() => { let on = true; setLogo(undefined); loadLogo(shop?.logo).then((l) => on && setLogo(l)).catch(() => on && setLogo(null)); return () => { on = false; }; }, [shop?.logo]);
  useEffect(() => {
    if (logo === undefined || !url) return;
    const t = setTimeout(() => {
      const qr = qrRef.current;
      if (!qr) return;
      const opts = { qr, logo, shop, url, lang, offer: offer.trim() };
      const c = size === "a4" ? drawA4(opts) : drawA6Tent(opts);
      canvasRef.current = c;
      setPreview(c.toDataURL("image/jpeg", 0.6));
    }, 250);
    return () => clearTimeout(t);
  }, [logo, lang, size, offer, url, shop?.name, shop?.hours_label]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!url) return null;
  const key = String(shop.slug || shop.shop_id).replace(/[^a-z0-9-]/gi, "").slice(0, 40) || "shop";
  const download = async () => {
    if (!canvasRef.current) { toast.error("Preview not ready, please wait a moment"); return; }
    try {
      await downloadCanvas(canvasRef.current, size === "a4" ? `verb-qr-poster-a4-${lang}-${key}.png` : `verb-table-tent-a6-${lang}-${key}.png`);
      toast.success(size === "a4" ? "A4 poster downloaded — print on A4 at 100% (actual size)." : "Table tent downloaded — print on A5 (or A4 'fit'), fold along the dashed line, stand on the counter.", { duration: 6000 });
      if (shop.logo && !logo) toast.info("Logo could not be loaded, so the poster was made without it.");
    } catch { toast.error("Could not create the poster"); }
  };
  const saveOffer = async () => {
    setSaving(true);
    try { await vendorUpdateShop(shop.shop_id, { poster_offer: offer.trim() }); toast.success(offer.trim() ? "Offer line saved" : "Offer line removed"); onSaved?.(); }
    catch { toast.error("Could not save the offer line"); } finally { setSaving(false); }
  };
  const sel = "rounded-full border border-slate-200 bg-white px-3 py-2 text-sm min-w-0";
  return (
    <div className="grid sm:grid-cols-[1fr_auto] gap-3 items-start min-w-0" data-testid={`${testid}-studio`}>
      <div className="space-y-2 min-w-0">
        <div className="flex flex-wrap gap-2">
          <select value={size} onChange={(e) => setSize(e.target.value)} className={sel} aria-label="Poster size" data-testid={`${testid}-size-select`}>
            <option value="a4">A4 QR Poster</option>
            <option value="a6">A6 Table Tent</option>
          </select>
          <select value={lang} onChange={(e) => setLang(e.target.value)} className={sel} aria-label="Poster language order" data-testid={`${testid}-lang-select`}>
            <option value="en">English-first</option>
            <option value="hi">Hindi-first / हिंदी पहले</option>
          </select>
        </div>
        <div className="flex items-center gap-2 min-w-0">
          <input value={offer} onChange={(e) => setOffer(cleanOffer(e.target.value))} maxLength={OFFER_MAX} placeholder="Offer line (optional), e.g. Free home delivery" className="flex-1 min-w-0 border border-slate-200 rounded-full px-3 py-2 text-sm outline-none focus:border-emerald-500" data-testid={`${testid}-offer-input`} />
          <button type="button" onClick={saveOffer} disabled={saving} className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-2 text-xs font-semibold flex-shrink-0 disabled:opacity-60" data-testid={`${testid}-offer-save`}>
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save
          </button>
        </div>
        <div className="text-[11px] text-slate-500" data-testid={`${testid}-offer-count`}>{offer.length}/{OFFER_MAX} · only what you type is printed{shop.hours_label ? ` · Timing: ${shop.hours_label}` : " · no timing saved (add it in Shop Profile)"}</div>
        <button type="button" onClick={download} disabled={!preview} className="inline-flex items-center gap-1.5 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white px-4 py-2 text-sm font-semibold disabled:opacity-60" data-testid={`${testid}-download-btn`}>
          <Download className="w-4 h-4" /> {size === "a4" ? "Download A4 QR Poster" : "Download A6 Table Tent"}
        </button>
        {size === "a6" && <div className="text-[11px] text-slate-500" data-testid={`${testid}-print-help`}>Prints on A5 (or A4 “fit to page”). Fold along the dashed line so the QR faces both sides.</div>}
      </div>
      <div className="w-36 sm:w-40 justify-self-center rounded-lg border border-slate-200 bg-slate-50 overflow-hidden" data-testid={`${testid}-preview`}>
        {preview ? <img src={preview} alt="Poster preview" className="w-full h-auto block" data-testid={`${testid}-preview-img`} /> : <div className="aspect-[1/1.41] flex items-center justify-center"><Loader2 className="w-4 h-4 animate-spin text-slate-400" /></div>}
      </div>
      <QRCodeCanvas ref={qrRef} value={url} size={800} level="M" marginSize={4} style={{ display: "none" }} />
    </div>
  );
}

export function ShopLinkTools({ shop }) {
  const url = shopPublicUrl(shop);
  if (!url) return null;
  return (
    <div className="mb-4 min-w-0" data-testid="shop-link-tools">
      <div className="flex flex-col min-[420px]:flex-row min-[420px]:items-center gap-2 min-w-0 bg-white border border-slate-200 rounded-2xl min-[420px]:rounded-full p-1.5 min-[420px]:ps-4 min-[420px]:pe-1 min-[420px]:py-1">
        <span className="text-xs text-slate-600 truncate min-w-0 flex-1 px-2 min-[420px]:px-0 py-1 min-[420px]:py-0" title={url} data-testid="shop-link-url">{url}</span>
        <button type="button" onClick={() => copyShopLink(shop)} className="inline-flex items-center justify-center gap-1.5 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white px-3 py-2 min-h-[40px] min-[420px]:min-h-[36px] min-[420px]:py-1.5 text-xs font-semibold flex-shrink-0 whitespace-nowrap" data-testid="copy-shop-link-btn">
          <Copy className="w-3.5 h-3.5" /> Copy Shop Link
        </button>
      </div>
    </div>
  );
}
